import React, { useState, useEffect, useRef } from "react";
import RadioButton from "../../Buttons/RadioButton";
import { ENotificationTest } from "../../../Model/ENotificationTest";
import MtiButton from "../../Buttons/MtiButton";
import { SmtpSetting } from "../../../Model/SmtpSetting";
import ToolsApi from "../../../api/ToolsApi";
import "./ToolsEmail.css";
import { ENotificationType } from "../../../Model/ENotificationType";
import { sendNotification } from "../../../redux/actions/NotificationAction";
import { getDispatch } from "../../../redux/configureStore";

type Props = {
  disabled: boolean;
};

export default function PredefinedEmail({ disabled }: Props) {
  const dispatch = getDispatch();
  const [emailTestType, setEmailTestType] = useState(ENotificationTest.Email);
  const [senderEmail, setSenderEmail] = useState("");
  const [recipientEmails, setRecipientEmails] = useState("");
  const [smtpSetting, setSmtpSetting] = useState(
    undefined as SmtpSetting | undefined
  );
  const [showButtonMoreRecipient, setShowButtonMoreRecipient] = useState(false);
  const [showMoreRecipient, setShowMoreRecipient] = useState(false);
  const moreConfigInfoRef = useRef<HTMLDivElement>(null);

  const emailTestKeyValuePair = new Map<string, ENotificationTest>([
    ["email-test-exception", ENotificationTest.Exception],
    ["email-test-diagnostic", ENotificationTest.Diagnostics],
    ["email-test-temperature", ENotificationTest.Temperature],
  ]);

  useEffect(() => {
    ToolsApi.getSmtpSetting().then((smtp) => {
      setSmtpSetting(smtp);
    });
  }, []);

  const onChangeEmailTest = (id: string) => {
    setEmailTestType(emailTestKeyValuePair.get(id) ?? ENotificationTest.Email);
  };

  const onClickSend = () => {
    // testEmail field is not used on predefined email test
    ToolsApi.sendTestEmail({
      notificationTest: emailTestType,
      testEmail: {
        smtp: smtpSetting ?? {
          server: "-",
          port: 25,
          username: "-",
          password: "-",
          enableSsl: false,
        },
        to: recipientEmails,
        from: senderEmail,
        subject: "-",
        body: "-",
      },
    }).then(
      (succes) => {
        dispatch(
          sendNotification(ENotificationType.Success, "Email has been sent.")
        );
      },
      (error) => console.log(error)
    );
  };

  useEffect(() => {
    ToolsApi.getNotificationTestRecipientEmails(emailTestType).then(
      (emails) => {
        const recipientText = emails.join(", ");
        setRecipientEmails(recipientText);
        if (recipientText.length > 49) {
          setShowButtonMoreRecipient(true);
        }
      }
    );
  }, [emailTestType]);

  useEffect(() => {
    ToolsApi.getNotificationTestSenderEmail().then((result) => {
      setSenderEmail(result);
    });
  }, []);

  useEffect(() => {
    const onClickOutsideMoreButton = (event: MouseEvent) => {
      if (
        moreConfigInfoRef.current &&
        showMoreRecipient &&
        !moreConfigInfoRef.current.contains(event.target as HTMLElement)
      ) {
        setShowMoreRecipient(false);
      }
    };

    document.addEventListener("mousedown", onClickOutsideMoreButton);

    return () =>
      document.removeEventListener("mousedown", onClickOutsideMoreButton);
  }, [moreConfigInfoRef, showMoreRecipient]);

  return (
    <>
      <div className="tools-email-type">
        <div>Email Type</div>
        <RadioButton
          id="email-test-exception"
          name="email-test-exception"
          label="Unhandled Exception"
          checked={emailTestType === ENotificationTest.Exception}
          onChange={onChangeEmailTest}
          color="alternate"
          disabled={disabled}
          style={{
            fontSize: 16,
          }}
        />
        <RadioButton
          id="email-test-diagnostic"
          name="email-test-diagnostic"
          label="Diagnostics"
          checked={emailTestType === ENotificationTest.Diagnostics}
          onChange={onChangeEmailTest}
          color="alternate"
          disabled={disabled}
          style={{
            fontSize: 16,
          }}
        />
        <RadioButton
          id="email-test-temperature"
          name="email-test-temperature"
          label="Temperature Notification"
          checked={emailTestType === ENotificationTest.Temperature}
          onChange={onChangeEmailTest}
          color="alternate"
          disabled={disabled}
          style={{
            fontSize: 16,
          }}
        />
      </div>
      <div className="tools-email-send">
        <MtiButton
          variant="primary"
          size="medium"
          style={{
            paddingLeft: 16,
            paddingRight: 16,
          }}
          onClick={onClickSend}
          disabled={emailTestType === ENotificationTest.Email || disabled}
        >
          SEND
        </MtiButton>
      </div>
      <div className="tools-email-config">
        <div className="tools-email-">Configuration</div>
        <div className="tools-email-config-line">
          <div>Server</div>
          <div>{smtpSetting?.server ?? ""}</div>
        </div>
        <div className="tools-email-config-line">
          <div>Port</div>
          <div>{smtpSetting?.port ?? ""}</div>
        </div>
        <div className="tools-email-config-line">
          <div>User Name</div>
          <div>{smtpSetting?.username ?? ""}</div>
        </div>
        <div className="tools-email-config-line">
          <div>Sender</div>
          <div>{senderEmail}</div>
        </div>
        <div className="tools-email-config-line" style={{ padding: 0 }}>
          <div>Recipient</div>
          <div>{recipientEmails}</div>
          {showButtonMoreRecipient && (
            <div style={{ position: "relative" }} ref={moreConfigInfoRef}>
              <MtiButton
                variant="tertiary"
                colorVariant="alternate"
                size="small"
                style={{ minHeight: 26 }}
                onClick={() => setShowMoreRecipient(!showMoreRecipient)}
              >
                MORE
              </MtiButton>
              <div
                className="config-more-info"
                style={{ display: showMoreRecipient ? "block" : "none" }}
              >
                {recipientEmails}
              </div>
            </div>
          )}
        </div>
        <div className="tools-email-config-footer">
          These configurations could be configured under Settings &gt;
          Preferences
        </div>
      </div>
    </>
  );
}
